import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTicketAlt,
  faCalendarAlt,
  faPercent,
  faCoffee,
  faParking,
  faMobileAlt,
  faGift,
  faArrowRight,
  faCheck,
  faShieldAlt,
  faHome,
  faTag,
  faBook,
  faHandshake,
  faClock,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

const MEMBERSHIPS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

// ── Page shell ────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

// ── Section primitives ────────────────────────────────────────────────────────

const Section = styled.section`
  padding: clamp(3rem, 7vw, 5rem) clamp(1.25rem, 4vw, 2rem);
  ${props => props.$bg && `background: ${props.$bg};`}
  ${props => props.$borderTop && `border-top: 1px solid rgba(16, 29, 64, 0.07);`}
`;

const Inner = styled.div`
  max-width: ${props => props.$wide ? '1200px' : '1080px'};
  margin: 0 auto;
`;

// ── Section header ────────────────────────────────────────────────────────────

const SectionEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.48);
  margin: 0 0 0.65rem;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.9rem, 4.2vw, 2.75rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  line-height: 1.12;
  text-align: center;

  &::after {
    content: '';
    display: block;
    width: 3rem;
    height: 2px;
    margin: 1.15rem auto 0;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => props.theme.colors.accent},
      transparent
    );
    opacity: 0.85;
  }
`;

const SectionSubtitle = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.95rem, 1.9vw, 1.08rem);
  line-height: 1.65;
  color: rgba(16, 29, 64, 0.62);
  margin: 0 auto 3rem;
  max-width: 38rem;
  text-align: center;
`;

// ── Comparison cards ──────────────────────────────────────────────────────────

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto 1fr auto;
  gap: 1.5rem;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const ComparisonCard = styled.div`
  grid-row: span 3;
  display: grid;
  grid-template-rows: subgrid;
  border-radius: 20px;
  border: 1px solid
    ${props =>
      props.$featured
        ? 'rgba(157, 109, 73, 0.38)'
        : 'rgba(16, 29, 64, 0.08)'};
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: ${props =>
    props.$featured
      ? '0 0 0 1px rgba(157, 109, 73, 0.18), 0 16px 48px rgba(16, 29, 64, 0.12), 0 2px 0 rgba(255, 255, 255, 0.9) inset'
      : '0 8px 32px rgba(16, 29, 64, 0.07), 0 1px 0 rgba(255, 255, 255, 0.85) inset'};
  overflow: hidden;
  position: relative;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  @media (max-width: 900px) {
    grid-row: auto;
    display: flex;
    flex-direction: column;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 56px rgba(16, 29, 64, 0.13),
      0 2px 0 rgba(255, 255, 255, 0.9) inset;
  }
`;

const HXComparisonCard = styled(ComparisonCard)`
  background: linear-gradient(160deg, #2a0d1e 0%, #3d1535 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(42, 13, 30, 0.35),
    0 1px 0 rgba(255, 255, 255, 0.06) inset;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 56px rgba(42, 13, 30, 0.45),
      0 1px 0 rgba(255, 255, 255, 0.06) inset;
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.colors.accent};
  color: #fff;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 0.35rem 1.1rem;
  border-radius: 0 0 10px 10px;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(157, 109, 73, 0.35);
`;

const PartnerBadge = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 0.35rem 1.1rem;
  border-radius: 0 0 10px 10px;
  white-space: nowrap;
  backdrop-filter: blur(6px);
`;

const CardHeader = styled.div`
  padding: 2.75rem 1.75rem 1.5rem;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);
`;

const HXCardHeader = styled(CardHeader)`
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
`;

const CardBrand = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${props => (props.$dark ? 'rgba(255,255,255,0.45)' : 'rgba(16, 29, 64, 0.4)')};
  margin: 0 0 0.35rem;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.25rem, 2.4vw, 1.6rem);
  font-weight: 700;
  color: ${props => (props.$dark ? '#fff' : props.theme.colors.primary)};
  margin: 0 0 0.35rem;
  line-height: 1.2;
`;

const CardSubtitle = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.825rem;
  color: ${props => (props.$dark ? 'rgba(255,255,255,0.55)' : 'rgba(16, 29, 64, 0.52)')};
  margin: 0 0 1.1rem;
  line-height: 1.45;
  min-height: 2.4rem;
`;

const CardPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
`;

const PriceFrom = styled.span`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${props => (props.$dark ? 'rgba(255,255,255,0.45)' : 'rgba(16, 29, 64, 0.42)')};
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const PriceAmount = styled.span`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.75rem, 3.5vw, 2.2rem);
  font-weight: 700;
  color: ${props => (props.$dark ? '#fff' : props.theme.colors.primary)};
  line-height: 1;
`;

const PriceUnit = styled.span`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  color: ${props => (props.$dark ? 'rgba(255,255,255,0.45)' : 'rgba(16, 29, 64, 0.42)')};
`;

const CardFeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 1.5rem 1.75rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  align-self: start;

  @media (max-width: 900px) {
    flex: 1;
  }
`;

const CardFeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${props => (props.$dark ? 'rgba(255,255,255,0.72)' : 'rgba(16, 29, 64, 0.7)')};

  svg {
    color: ${props => (props.$dark ? '#d4a574' : props.theme.colors.accent)};
    flex-shrink: 0;
    margin-top: 0.22rem;
    font-size: 0.7rem;
  }
`;

const CardCTA = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0 1.75rem 1.75rem;
  padding: 0.82rem 1.4rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  align-self: stretch;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease,
    color 0.18s ease;
  background: ${props => (props.$dark ? 'rgba(255,255,255,0.12)' : props.theme.colors.accent)};
  color: #fff;
  border: ${props => (props.$dark ? '1px solid rgba(255,255,255,0.22)' : 'none')};
  box-shadow: ${props =>
    props.$dark ? 'none' : '0 4px 14px rgba(157, 109, 73, 0.32)'};

  @media (max-width: 900px) {
    margin-top: auto;
  }

  &:hover {
    transform: translateY(-1px);
    background: ${props => (props.$dark ? 'rgba(255,255,255,0.2)' : '#7a5238')};
    box-shadow: ${props =>
      props.$dark ? 'none' : '0 6px 18px rgba(157, 109, 73, 0.42)'};
  }
`;

// ── Detail tabs (Castle / Parkland full breakdown) ────────────────────────────

const DetailSection = styled.section`
  padding: clamp(2.5rem, 5vw, 3.5rem) clamp(1.25rem, 4vw, 2rem);
  background: ${props => props.theme.colors.offWhite};
  border-top: 1px solid rgba(16, 29, 64, 0.07);
`;

const TabRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2.5rem;
  border: 1.5px solid rgba(16, 29, 64, 0.12);
  border-radius: 999px;
  overflow: hidden;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  background: rgba(255, 255, 255, 0.6);
`;

const Tab = styled.button`
  background: ${props =>
    props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props =>
    props.$active ? '#fff' : 'rgba(16, 29, 64, 0.58)'};
  border: none;
  padding: 0.75rem 2.2rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: ${props => (props.$active ? '#fff' : props.theme.colors.primary)};
    background: ${props =>
      props.$active ? props.theme.colors.primary : 'rgba(16, 29, 64, 0.04)'};
  }
`;

const DetailPanel = styled.div`
  max-width: 820px;
  margin: 0 auto;
  background: #fff;
  border-radius: 20px;
  border: 1px solid rgba(16, 29, 64, 0.08);
  box-shadow: 0 8px 40px rgba(16, 29, 64, 0.07),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
  overflow: hidden;
`;

const DetailPanelHeader = styled.div`
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.secondary} 100%
  );
  padding: 2rem 2.5rem;

  @media (max-width: 640px) {
    padding: 1.75rem 1.5rem;
  }
`;

const DetailPanelTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.4rem, 3vw, 1.9rem);
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.3rem;
`;

const DetailPanelSub = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
`;

const DetailBody = styled.div`
  padding: 2rem 2.5rem;

  @media (max-width: 640px) {
    padding: 1.5rem;
  }
`;

const DetailSectionLabel = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  margin: 0 0 0.9rem;
`;

// Pricing table

const PricingTable = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(16, 29, 64, 0.08);
  margin-bottom: 2rem;
`;

const PricingRow = styled.div`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(16, 29, 64, 0.06);
  }

  &:first-child {
    background: ${props => props.theme.colors.primary};
  }

  &:nth-child(even):not(:first-child) {
    background: rgba(16, 29, 64, 0.025);
  }
`;

const PricingCell = styled.div`
  padding: ${props => (props.$header ? '0.75rem 1.1rem' : '0.9rem 1.1rem')};
  flex: ${props => (props.$wide ? 2 : 1)};
  font-family: system-ui, -apple-system, sans-serif;
  font-size: ${props => (props.$header ? '0.72rem' : '0.9rem')};
  color: ${props => (props.$header ? '#fff' : 'rgba(16, 29, 64, 0.78)')};
  font-weight: ${props => (props.$header ? '700' : '400')};
  text-align: ${props => (props.$right ? 'right' : 'left')};
  letter-spacing: ${props => (props.$header ? '0.1em' : '0')};
  text-transform: ${props => (props.$header ? 'uppercase' : 'none')};
`;

// Benefits list in detail panel

const BenefitsList = styled.ul`
  list-style: none;
  margin: 0 0 1.75rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.925rem;
  line-height: 1.55;
  color: rgba(16, 29, 64, 0.72);

  svg {
    color: ${props => props.theme.colors.accent};
    flex-shrink: 0;
    margin-top: 0.22rem;
  }
`;

const NoteText = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.8rem;
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.45);
  font-style: italic;
  margin: 0 0 0.4rem;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.9rem;
  margin-top: 2rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.9rem 1.8rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  text-decoration: none;
  background: ${props => props.theme.colors.accent};
  color: #fff;
  box-shadow: 0 4px 14px rgba(157, 109, 73, 0.32);
  flex: 1;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(157, 109, 73, 0.42);
    background: #7a5238;
  }
`;

const SecondaryBtn = styled(PrimaryBtn)`
  background: transparent;
  color: ${props => props.theme.colors.accent};
  border: 1.5px solid ${props => props.theme.colors.accent};
  box-shadow: none;

  &:hover {
    background: ${props => props.theme.colors.accent};
    color: #fff;
    box-shadow: 0 4px 14px rgba(157, 109, 73, 0.32);
  }
`;

// ── HeritageXplore Club partner section ───────────────────────────────────────

const HXSection = styled.section`
  padding: clamp(3.5rem, 8vw, 5.5rem) clamp(1.25rem, 4vw, 2rem);
  background: linear-gradient(160deg, #1a0811 0%, #2d1430 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.04);
`;

const HXInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

const HXTopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2rem;
  align-items: flex-start;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HXBadge = styled.span`
  display: inline-block;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  margin-bottom: 1.1rem;
`;

const HXTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.9rem, 4.5vw, 3rem);
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.65rem;
  line-height: 1.12;
`;

const HXSubtitle = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.92rem, 1.8vw, 1.02rem);
  color: rgba(255, 255, 255, 0.58);
  margin: 0;
  max-width: 44rem;
  line-height: 1.65;
`;

const HXPriceCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  padding: 1.5rem 1.75rem;
  text-align: center;
  min-width: 180px;
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    text-align: left;
    min-width: 0;
  }
`;

const HXPriceFigure = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  justify-content: center;
  margin-bottom: 0.3rem;

  @media (max-width: 768px) {
    margin-bottom: 0;
    flex-shrink: 0;
  }
`;

const HXPriceMain = styled.span`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(2.2rem, 5vw, 3rem);
  font-weight: 700;
  color: #fff;
  line-height: 1;
`;

const HXPriceUnit = styled.span`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.48);
`;

const HXPriceLabel = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.48);
  margin: 0;
  line-height: 1.4;
`;

const HXGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2.5rem;

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const HXBenefitCard = styled.div`
  padding: 1.25rem 1.3rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.09);
  transition: background 0.22s ease, border-color 0.22s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
    border-color: rgba(255, 255, 255, 0.18);
  }
`;

const HXBenefitIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: rgba(212, 165, 116, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  color: #d4a574;
  font-size: 0.9rem;
`;

const HXBenefitTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.35rem;
  line-height: 1.2;
`;

const HXBenefitText = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.52);
  margin: 0;
`;

const HXCTARow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
`;

const HXMemberBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem 2.2rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  text-decoration: none;
  background: #fff;
  color: #2a0d1e;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.24);
  white-space: nowrap;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.32);
  }
`;

const HXLearnMore = styled.a`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: color 0.18s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const HXDisclaimer = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.3);
  margin: 1.5rem 0 0;
  font-style: italic;
`;

// ── Heritage support section ──────────────────────────────────────────────────

const SupportSection = styled.section`
  padding: clamp(3.5rem, 8vw, 5.5rem) clamp(1.25rem, 4vw, 2rem);
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.secondary} 100%
  );
`;

const SupportInner = styled.div`
  max-width: 840px;
  margin: 0 auto;
  text-align: center;
`;

const SupportEyebrow = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  margin: 0 0 0.9rem;
`;

const SupportTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.9rem, 4.5vw, 3rem);
  font-weight: 700;
  color: #fff;
  margin: 0 0 1.5rem;
  line-height: 1.15;
`;

const SupportText = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.9vw, 1.05rem);
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.65);
  margin: 0 auto 1.1rem;
  max-width: 38rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

// ── Benefits grid ─────────────────────────────────────────────────────────────

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitGridCard = styled.div`
  padding: 1.65rem 1.5rem;
  border-radius: 16px;
  border: 1px solid rgba(16, 29, 64, 0.08);
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 8px 32px rgba(16, 29, 64, 0.07),
    0 1px 0 rgba(255, 255, 255, 0.85) inset;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(157, 109, 73, 0.22);
    box-shadow: 0 12px 40px rgba(16, 29, 64, 0.1),
      0 1px 0 rgba(255, 255, 255, 0.9) inset;
  }
`;

const BenefitGridIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(157, 109, 73, 0.09);
  border: 1px solid rgba(157, 109, 73, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.accent};
  font-size: 1.05rem;
  margin-bottom: 1.1rem;
`;

const BenefitGridTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
`;

const BenefitGridText = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.875rem;
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.6);
  margin: 0;
`;

const ComingSoonTag = styled.span`
  display: inline-block;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  background: rgba(157, 109, 73, 0.1);
  border: 1px solid rgba(157, 109, 73, 0.2);
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  margin-left: 0.45rem;
  vertical-align: middle;
`;

// ── Contact section ───────────────────────────────────────────────────────────

const ContactSection = styled.section`
  padding: clamp(2.5rem, 5vw, 3.5rem) clamp(1.25rem, 4vw, 2rem);
  text-align: center;
  background: ${props => props.theme.colors.offWhite};
  border-top: 1px solid rgba(16, 29, 64, 0.07);
`;

const ContactText = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.92rem, 1.8vw, 1.02rem);
  line-height: 1.65;
  color: rgba(16, 29, 64, 0.6);
  max-width: 44rem;
  margin: 0 auto 0.5rem;
`;

const ContactLink = styled.a`
  color: ${props => props.theme.colors.accent};
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const TermsText = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  color: rgba(16, 29, 64, 0.38);
  font-style: italic;
  margin: 0.5rem auto 0;
`;

// ── Component ─────────────────────────────────────────────────────────────────

const Memberships = () => {
  const [activeTab, setActiveTab] = useState('castle');

  const castlePricing = [
    { type: 'Adult', price: '£52' },
    { type: 'Family (1 adult, 3 children)', price: '£75' },
    { type: 'Family (2 adults, 3 children)', price: '£125' },
    { type: 'Child (aged 3–16)', price: '£20' },
    { type: 'Child (under 3)', price: 'Free' },
  ];

  const parklandPricing = [
    { type: 'Adult', price: '£25' },
    { type: 'Family (1 adult, 3 children)', price: '£65' },
    { type: 'Family (2 adults, 3 children)', price: '£85' },
    { type: 'Child (aged 3–16)', price: '£15' },
    { type: 'Child (under 3)', price: 'Free' },
  ];

  const castleBenefits = [
    { icon: faTicketAlt, text: 'Unlimited access to the castle, parkland, gardens and playground on all open days' },
    { icon: faCalendarAlt, text: "Free access to all Belvoir ran events (excluding evening concerts, Circus and Bonfire Night)" },
    { icon: faPercent, text: '10% off at Vale View Café, Belvoir Bistro, Gift Shop, Farm Shop, Balloon Bar and Duchess Collection' },
    { icon: faParking, text: 'Free parking for one vehicle in the main ticket office car park' },
    { icon: faPercent, text: "10% off Belvoir Estate 'Stay with Us' rental properties" },
    { icon: faMobileAlt, text: 'Receive points on the Belvoir Castle app and personal offers' },
    { icon: faCoffee, text: 'Digital coffee stamps in our new state-of-the-art app' },
  ];

  const parklandBenefits = [
    { icon: faTicketAlt, text: 'Unlimited access to the parkland, gardens and playground on all open days' },
    { icon: faCalendarAlt, text: "Free access to Belvoir's outdoor events (excluding external events)" },
    { icon: faPercent, text: '10% off at Vale View Café, Belvoir Bistro, Gift Shop, Farm Shop, Balloon Bar and Duchess Collection' },
    { icon: faParking, text: 'Free parking for one vehicle in the main ticket office car park' },
    { icon: faPercent, text: "10% off Belvoir Estate 'Stay with Us' rental properties" },
    { icon: faMobileAlt, text: 'Receive points on the Belvoir Castle app and personal offers' },
    { icon: faCoffee, text: 'Digital coffee stamps in our new state-of-the-art app' },
  ];

  const hxBenefits = [
    {
      icon: faHome,
      title: 'Spectacular stays',
      text: "Sleep in Britain's most storied places — castle wings, estate cottages, clock towers and unexpected places in between.",
    },
    {
      icon: faTag,
      title: 'Pay less, explore more',
      text: 'Book heritage stays and exclusive events with members-only pricing across the network.',
    },
    {
      icon: faCalendarAlt,
      title: 'Extraordinary events',
      text: 'Enjoy exclusive HeritageXplore events — candlelit evenings, supper clubs and artist residencies — at preferred rates.',
    },
    {
      icon: faClock,
      title: 'Priority booking',
      text: 'Hear about new releases 48 hours before the public and book before anyone else.',
    },
    {
      icon: faHandshake,
      title: 'Partner privileges',
      text: 'Member pricing across a network of heritage houses, artisan makers and like-minded brands.',
    },
    {
      icon: faBook,
      title: 'Book Club access',
      text: 'Full access to the HeritageXplore book club and its curated reading programme.',
    },
  ];

  return (
    <Page>
      <ParallaxBanner
        title="Memberships"
        subtitle="Become a Friend of Belvoir Castle"
        image={MEMBERSHIPS_BANNER_CDN}
      />

      {/* ── Overview comparison cards ── */}
      <Section>
        <Inner>
          <SectionEyebrow>Membership Options</SectionEyebrow>
          <SectionTitle>Friends of Belvoir</SectionTitle>
          <SectionSubtitle>
            Enjoy unlimited access, exclusive discounts and a range of rewards — and help sustain one of England's finest historic estates for generations to come.
          </SectionSubtitle>

          <ComparisonGrid>
            {/* Castle card */}
            <ComparisonCard $featured>
              <PopularBadge>Most popular</PopularBadge>
              <CardHeader>
                <CardBrand>Belvoir Castle</CardBrand>
                <CardTitle>Castle Membership</CardTitle>
                <CardSubtitle>Castle, Gardens &amp; Playground</CardSubtitle>
                <CardPriceRow>
                  <PriceFrom>from</PriceFrom>
                  <PriceAmount>£52</PriceAmount>
                  <PriceUnit>/ year</PriceUnit>
                </CardPriceRow>
              </CardHeader>
              <CardFeatureList>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  Unlimited castle, gardens &amp; playground access
                </CardFeatureItem>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  Free access to Belvoir ran events
                </CardFeatureItem>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  10% off cafés, shops &amp; accommodation
                </CardFeatureItem>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  Free car parking included
                </CardFeatureItem>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  Digital rewards &amp; coffee stamps via app
                </CardFeatureItem>
              </CardFeatureList>
              <CardCTA href="/booking-journey?locationId=978&pageId=37bd858f-4f04-4e83-a469-05274db0d546">
                Become a Castle Friend <FontAwesomeIcon icon={faArrowRight} />
              </CardCTA>
            </ComparisonCard>

            {/* Parkland card */}
            <ComparisonCard>
              <CardHeader>
                <CardBrand>Belvoir Castle</CardBrand>
                <CardTitle>Parkland Membership</CardTitle>
                <CardSubtitle>Gardens &amp; Playground</CardSubtitle>
                <CardPriceRow>
                  <PriceFrom>from</PriceFrom>
                  <PriceAmount>£25</PriceAmount>
                  <PriceUnit>/ year</PriceUnit>
                </CardPriceRow>
              </CardHeader>
              <CardFeatureList>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  Unlimited parkland, gardens &amp; playground access
                </CardFeatureItem>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  Free access to Belvoir's outdoor events
                </CardFeatureItem>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  10% off cafés, shops &amp; accommodation
                </CardFeatureItem>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  Free car parking included
                </CardFeatureItem>
                <CardFeatureItem>
                  <FontAwesomeIcon icon={faCheck} />
                  Digital rewards via Belvoir Castle app
                </CardFeatureItem>
              </CardFeatureList>
              <CardCTA href="/booking-journey?locationId=978&pageId=9d7c060b-6088-4bc1-8def-7bddb9b87ef9">
                Become a Parkland Friend <FontAwesomeIcon icon={faArrowRight} />
              </CardCTA>
            </ComparisonCard>

            {/* HeritageXplore card */}
            <HXComparisonCard>
              <PartnerBadge>Partner membership</PartnerBadge>
              <HXCardHeader>
                <CardBrand $dark>HeritageXplore</CardBrand>
                <CardTitle $dark>HX Club</CardTitle>
                <CardSubtitle $dark>
                  Britain's independent historic houses
                </CardSubtitle>
                <CardPriceRow>
                  <PriceFrom $dark>from</PriceFrom>
                  <PriceAmount $dark>£20</PriceAmount>
                  <PriceUnit $dark>/ year</PriceUnit>
                </CardPriceRow>
              </HXCardHeader>
              <CardFeatureList>
                <CardFeatureItem $dark>
                  <FontAwesomeIcon icon={faCheck} />
                  Curated heritage stays across Britain
                </CardFeatureItem>
                <CardFeatureItem $dark>
                  <FontAwesomeIcon icon={faCheck} />
                  Priority booking — 48 hours early access
                </CardFeatureItem>
                <CardFeatureItem $dark>
                  <FontAwesomeIcon icon={faCheck} />
                  Exclusive events at member rates
                </CardFeatureItem>
                <CardFeatureItem $dark>
                  <FontAwesomeIcon icon={faCheck} />
                  Partner network privileges
                </CardFeatureItem>
                <CardFeatureItem $dark>
                  <FontAwesomeIcon icon={faCheck} />
                  HeritageXplore Book Club access
                </CardFeatureItem>
              </CardFeatureList>
              <CardCTA
                $dark
                href="https://www.heritagexplore.com/hx-club/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Explore HX Club <FontAwesomeIcon icon={faExternalLinkAlt} />
              </CardCTA>
            </HXComparisonCard>
          </ComparisonGrid>
        </Inner>
      </Section>

      {/* ── Belvoir membership full detail ── */}
      <DetailSection>
        <Inner>
          <TabRow>
            <Tab $active={activeTab === 'castle'} onClick={() => setActiveTab('castle')}>
              Castle Membership
            </Tab>
            <Tab $active={activeTab === 'parkland'} onClick={() => setActiveTab('parkland')}>
              Parkland Membership
            </Tab>
          </TabRow>

          {activeTab === 'castle' && (
            <DetailPanel>
              <DetailPanelHeader>
                <DetailPanelTitle>Castle Membership</DetailPanelTitle>
                <DetailPanelSub>Friends of Belvoir — Castle, Gardens &amp; Playground</DetailPanelSub>
              </DetailPanelHeader>
              <DetailBody>
                <DetailSectionLabel>Annual pricing</DetailSectionLabel>
                <PricingTable>
                  <PricingRow>
                    <PricingCell $wide $header>Membership type</PricingCell>
                    <PricingCell $right $header>Annual price</PricingCell>
                  </PricingRow>
                  {castlePricing.map((item, i) => (
                    <PricingRow key={i}>
                      <PricingCell $wide>{item.type}</PricingCell>
                      <PricingCell $right>{item.price}</PricingCell>
                    </PricingRow>
                  ))}
                </PricingTable>

                <DetailSectionLabel>What's included</DetailSectionLabel>
                <BenefitsList>
                  {castleBenefits.map((b, i) => (
                    <BenefitItem key={i}>
                      <FontAwesomeIcon icon={b.icon} />
                      {b.text}
                    </BenefitItem>
                  ))}
                </BenefitsList>

                <NoteText>
                  *Castle pass includes all outdoor daytime events plus indoor events: Halloween and Christmas at The Engine Yard.
                </NoteText>
                <NoteText>
                  Collect your Membership Pass during your first visit at the Visitor Hub.
                </NoteText>

                <ActionRow>
                  <PrimaryBtn href="/booking-journey?locationId=978&pageId=37bd858f-4f04-4e83-a469-05274db0d546">
                    Become a Castle Friend <FontAwesomeIcon icon={faArrowRight} />
                  </PrimaryBtn>
                  <SecondaryBtn href="https://online1.venpos.net/VenPosJourneyUI/WebSaleItems?id=439912c8-3e01-405b-82b5-6a787f45013d&type=WebPage&locationId=978&mainWebPageId=439912c8-3e01-405b-82b5-6a787f45013d&startId=439912c8-3e01-405b-82b5-6a787f45013d">
                    Give as a Gift <FontAwesomeIcon icon={faGift} />
                  </SecondaryBtn>
                </ActionRow>
              </DetailBody>
            </DetailPanel>
          )}

          {activeTab === 'parkland' && (
            <DetailPanel>
              <DetailPanelHeader>
                <DetailPanelTitle>Parkland Membership</DetailPanelTitle>
                <DetailPanelSub>Friends of Belvoir — Gardens &amp; Playground</DetailPanelSub>
              </DetailPanelHeader>
              <DetailBody>
                <DetailSectionLabel>Annual pricing</DetailSectionLabel>
                <PricingTable>
                  <PricingRow>
                    <PricingCell $wide $header>Membership type</PricingCell>
                    <PricingCell $right $header>Annual price</PricingCell>
                  </PricingRow>
                  {parklandPricing.map((item, i) => (
                    <PricingRow key={i}>
                      <PricingCell $wide>{item.type}</PricingCell>
                      <PricingCell $right>{item.price}</PricingCell>
                    </PricingRow>
                  ))}
                </PricingTable>

                <DetailSectionLabel>What's included</DetailSectionLabel>
                <BenefitsList>
                  {parklandBenefits.map((b, i) => (
                    <BenefitItem key={i}>
                      <FontAwesomeIcon icon={b.icon} />
                      {b.text}
                    </BenefitItem>
                  ))}
                </BenefitsList>

                <NoteText>
                  *Parkland pass covers Belvoir-run outdoor daytime events only. An upgrade fee is required to enter the Castle.
                </NoteText>
                <NoteText>
                  Collect your Membership Pass on your first visit at the Ticket Office or new Visitors Centre.
                </NoteText>

                <ActionRow>
                  <PrimaryBtn href="/booking-journey?locationId=978&pageId=9d7c060b-6088-4bc1-8def-7bddb9b87ef9">
                    Become a Parkland Friend <FontAwesomeIcon icon={faArrowRight} />
                  </PrimaryBtn>
                  <SecondaryBtn href="https://online1.venpos.net/VenPosJourneyUI/WebSaleItems?id=03496a0f-3859-453a-95aa-4afaa3652e77&type=WebPage&locationId=978&mainWebPageId=03496a0f-3859-453a-95aa-4afaa3652e77&startId=03496a0f-3859-453a-95aa-4afaa3652e77">
                    Give as a Gift <FontAwesomeIcon icon={faGift} />
                  </SecondaryBtn>
                </ActionRow>
              </DetailBody>
            </DetailPanel>
          )}
        </Inner>
      </DetailSection>

      {/* ── HeritageXplore Club partner section ── */}
      <HXSection>
        <HXInner>
          <HXTopRow>
            <div>
              <HXBadge>Partner membership</HXBadge>
              <HXTitle>HeritageXplore Club</HXTitle>
              <HXSubtitle>
                A members' club for those who want to go deeper into Britain's independent historic houses. Every month, a curated email lands in your inbox giving you exclusive access and privileges you won't find anywhere else.
              </HXSubtitle>
            </div>
            <HXPriceCard>
              <HXPriceFigure>
                <HXPriceMain>£20</HXPriceMain>
                <HXPriceUnit>/yr</HXPriceUnit>
              </HXPriceFigure>
              <HXPriceLabel>Annual membership</HXPriceLabel>
            </HXPriceCard>
          </HXTopRow>

          <HXGrid>
            {hxBenefits.map((b, i) => (
              <HXBenefitCard key={i}>
                <HXBenefitIcon>
                  <FontAwesomeIcon icon={b.icon} />
                </HXBenefitIcon>
                <HXBenefitTitle>{b.title}</HXBenefitTitle>
                <HXBenefitText>{b.text}</HXBenefitText>
              </HXBenefitCard>
            ))}
          </HXGrid>

          <HXCTARow>
            <HXMemberBtn
              href="https://www.heritagexplore.com/hx-club/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Become a member for £20 <FontAwesomeIcon icon={faExternalLinkAlt} />
            </HXMemberBtn>
            <HXLearnMore
              href="https://www.heritagexplore.com/hx-club/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about HeritageXplore <FontAwesomeIcon icon={faArrowRight} />
            </HXLearnMore>
          </HXCTARow>

          <HXDisclaimer>
            HeritageXplore Club is an independent partner membership. Terms and conditions apply.
          </HXDisclaimer>
        </HXInner>
      </HXSection>

      {/* ── Heritage support banner ── */}
      <SupportSection>
        <SupportInner>
          <SupportEyebrow>Why your membership matters</SupportEyebrow>
          <SupportTitle>Supporting Belvoir's Heritage</SupportTitle>
          <SupportText>
            Funds generated from the Friends scheme are invested in the conservation and upkeep of the House and Grounds — helping sustain and preserve them for future generations.
          </SupportText>
          <SupportText>
            By becoming a Friend of Belvoir, you're not just gaining access to a magnificent estate — you're helping protect an important piece of British heritage.
          </SupportText>
        </SupportInner>
      </SupportSection>

      {/* ── Benefits grid ── */}
      <Section $borderTop>
        <Inner>
          <SectionEyebrow>Member benefits</SectionEyebrow>
          <SectionTitle>Everything included with your membership</SectionTitle>
          <SectionSubtitle>
            Every Friends of Belvoir membership comes with these exclusive benefits.
          </SectionSubtitle>

          <BenefitsGrid>
            <BenefitGridCard>
              <BenefitGridIcon>
                <FontAwesomeIcon icon={faTicketAlt} />
              </BenefitGridIcon>
              <BenefitGridTitle>Unlimited Access</BenefitGridTitle>
              <BenefitGridText>
                Visit as often as you like on our open days. Explore the castle, gardens and adventure playground at your leisure.
              </BenefitGridText>
            </BenefitGridCard>

            <BenefitGridCard>
              <BenefitGridIcon>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </BenefitGridIcon>
              <BenefitGridTitle>Special Events</BenefitGridTitle>
              <BenefitGridText>
                Free access to Belvoir ran daytime events — from seasonal celebrations and artisan fairs to family fun days.
              </BenefitGridText>
            </BenefitGridCard>

            <BenefitGridCard>
              <BenefitGridIcon>
                <FontAwesomeIcon icon={faPercent} />
              </BenefitGridIcon>
              <BenefitGridTitle>Exclusive Discounts</BenefitGridTitle>
              <BenefitGridText>
                Save 10% on food, drinks and shopping across all our outlets, plus special offers on estate accommodation.
              </BenefitGridText>
            </BenefitGridCard>

            <BenefitGridCard>
              <BenefitGridIcon>
                <FontAwesomeIcon icon={faMobileAlt} />
              </BenefitGridIcon>
              <BenefitGridTitle>Digital Rewards</BenefitGridTitle>
              <BenefitGridText>
                Earn points and receive personalised offers through the Belvoir Castle app.
                <ComingSoonTag>Coming soon</ComingSoonTag>
              </BenefitGridText>
            </BenefitGridCard>

            <BenefitGridCard>
              <BenefitGridIcon>
                <FontAwesomeIcon icon={faCoffee} />
              </BenefitGridIcon>
              <BenefitGridTitle>Coffee Rewards</BenefitGridTitle>
              <BenefitGridText>
                Collect digital coffee stamps with every purchase and enjoy free drinks as your reward.
                <ComingSoonTag>Coming soon</ComingSoonTag>
              </BenefitGridText>
            </BenefitGridCard>

            <BenefitGridCard>
              <BenefitGridIcon>
                <FontAwesomeIcon icon={faShieldAlt} />
              </BenefitGridIcon>
              <BenefitGridTitle>Heritage Support</BenefitGridTitle>
              <BenefitGridText>
                Your membership directly contributes to the conservation and upkeep of Belvoir Castle, helping preserve this historic treasure.
              </BenefitGridText>
            </BenefitGridCard>
          </BenefitsGrid>
        </Inner>
      </Section>

      {/* ── Contact ── */}
      <ContactSection>
        <ContactText>
          To find out more or if you have any questions, please contact our membership team on{' '}
          <ContactLink href="tel:01476871001">01476 871001</ContactLink> or email{' '}
          <ContactLink href="mailto:fob@belvoircastle.com">fob@belvoircastle.com</ContactLink>
        </ContactText>
        <TermsText>Terms and conditions apply.</TermsText>
      </ContactSection>

      <Footer />
    </Page>
  );
};

export default Memberships;
